import type { CaptureResult, PostHogConfig, Properties } from 'posthog-js'

const posthogToken = import.meta.env.VITE_PUBLIC_POSTHOG_TOKEN?.trim() || ''
const posthogHost =
  import.meta.env.VITE_PUBLIC_POSTHOG_HOST?.trim() || 'https://eu.progettoarca.streetbeat.com'
const posthogUiHost =
  import.meta.env.VITE_PUBLIC_POSTHOG_UI_HOST?.trim() || 'https://eu.posthog.com'

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const OPAQUE_ID_RE = /^(?:[A-Z]{1,4}\d{2,}|[A-Za-z0-9_-]{10,})$/

export interface AnalyticsRoute {
  path: string
  route_view: string
  route_has_thread: boolean
  route_is_admin: boolean
}

function sanitizeSegment(segment: string, previous?: string): string {
  if (previous === 'threads' || previous === 'chat') return ':threadId'
  if (UUID_RE.test(segment)) return ':id'
  if (/^\d+$/.test(segment)) return ':id'
  if (OPAQUE_ID_RE.test(segment) && segment !== segment.toLowerCase()) return ':id'
  return segment
}

export function analyticsRouteFor(pathname: string, search = ''): AnalyticsRoute {
  const rawSegments = pathname.split('/').filter(Boolean)
  const segments = rawSegments.map((segment, index) =>
    sanitizeSegment(segment, rawSegments[index - 1]),
  )
  const normalizedPath = `/${segments.join('/')}`.replace(/\/+$/, '') || '/'
  const searchParams = new URLSearchParams(search)
  const hasThread = rawSegments.includes('chat') || Boolean(searchParams.get('thread_id'))

  return {
    path: normalizedPath,
    route_view: rawSegments[0] || 'home',
    route_has_thread: hasThread,
    route_is_admin: rawSegments[0] === 'admin',
  }
}

const POSTHOG_URL_PROPERTIES = new Set([
  '$current_url',
  '$initial_current_url',
  '$referrer',
  '$initial_referrer',
  '$session_entry_url',
  '$session_entry_referrer',
])

function analyticsUrlFor(value: unknown): unknown {
  if (typeof value !== 'string' || value === '$direct') return value
  try {
    const absolute = /^[a-z][a-z\d+.-]*:\/\//i.test(value)
    const url = new URL(value, 'https://analytics.invalid')
    const route = analyticsRouteFor(url.pathname)
    return absolute ? `${url.origin}${route.path}` : route.path
  } catch {
    return '[invalid-url]'
  }
}

function sanitizePostHogProperties(properties: Properties | undefined): Properties | undefined {
  if (!properties) return properties
  const sanitized = { ...properties }
  for (const key of POSTHOG_URL_PROPERTIES) {
    if (key in sanitized) sanitized[key] = analyticsUrlFor(sanitized[key])
  }
  for (const key of ['$set', '$set_once']) {
    const nested = sanitized[key]
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      sanitized[key] = sanitizePostHogProperties(nested as Properties)
    }
  }
  return sanitized
}

export function sanitizePostHogEvent(event: CaptureResult | null): CaptureResult | null {
  if (!event) return event
  return {
    ...event,
    properties: sanitizePostHogProperties(event.properties) ?? {},
    $set: sanitizePostHogProperties(event.$set),
    $set_once: sanitizePostHogProperties(event.$set_once),
  }
}

export const isPostHogEnabled = Boolean(posthogToken)
export const posthogApiKey = isPostHogEnabled ? posthogToken : null
export const posthogOptions: Partial<PostHogConfig> = {
  api_host: posthogHost,
  ui_host: posthogUiHost,
  autocapture: false,
  capture_pageview: false,
  capture_pageleave: false,
  capture_performance: false,
  capture_exceptions: false,
  logs: { captureConsoleLogs: false },
  defaults: '2026-01-30',
  // Evidence URLs can contain donor ids, search text, and cohort coordinates.
  // Session replay records the page URL separately from DOM/input masking.
  disable_session_recording: true,
  before_send: sanitizePostHogEvent,
}
